import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Rocket, Code, Book, ArrowRight } from 'lucide-react';

const UIUC: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "UIUC";

    return (
        <div className="min-h-screen bg-white font-sans text-[#13294B] selection:bg-[#E84A27] selection:text-white">
            {/* Top Bar - Orange */}
            <div className="bg-[#E84A27] text-white py-1.5 px-6 flex justify-between items-center text-xs font-bold uppercase tracking-wide">
                <div className="flex gap-4">
                    <a href="#" className="hover:underline">Alumni</a>
                    <a href="#" className="hover:underline">Athletics</a>
                </div>
                <div className="flex gap-4">
                    <a href="#" className="hover:underline">Directory</a>
                    <a href="#" className="hover:underline">My{schoolName.split(' ')[0]}</a>
                </div>
            </div>

            {/* Nav */}
            <nav className="bg-[#13294B] py-6 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <div className="bg-[#E84A27] text-white w-10 h-10 flex items-center justify-center font-serif font-bold text-2xl rounded-sm">I</div>
                        <div className="flex flex-col">
                            <span className="text-white font-bold text-lg leading-none uppercase tracking-wide">{schoolName}</span>
                            <span className="text-[#E84A27] font-bold text-xs uppercase tracking-wide">Urbana-Champaign</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-white text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#E84A27] transition-colors">About</a>
                        <a href="#" className="hover:text-[#E84A27] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#E84A27] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#E84A27] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#E84A27] transition-colors">Public Engagement</a>
                    </div>
                    
                    <div className="flex gap-4 text-white">
                         <Search className="cursor-pointer hover:text-[#E84A27]" size={22} />
                         <Menu className="cursor-pointer lg:hidden hover:text-[#E84A27]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Alma Mater */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1623838230784-dc8d9d592931?q=80&w=1974&auto=format&fit=crop" 
                    alt="Alma Mater Statue" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Diagonal Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#13294B]/90 via-transparent to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-xl text-white">
                         <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                             The Power of <br/>
                             <span className="text-[#E84A27]">I.</span>
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-light text-gray-200">
                             We pioneer innovative research and foster a community of original thinkers.
                         </p>
                         <Button className="bg-[#E84A27] hover:bg-[#c43b1d] text-white font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg">
                             Explore Degrees
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats / Pillars */}
            <section className="bg-white py-24 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="group">
                         <div className="w-20 h-20 bg-[#f0f0f0] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E84A27] transition-colors">
                            <Rocket size={32} className="text-[#13294B] group-hover:text-white transition-colors" />
                         </div>
                         <h3 className="text-2xl font-bold text-[#13294B] mb-4">Innovation</h3>
                         <p className="text-gray-600">Birthplace of the modern web browser, LED technology, and MRI.</p>
                     </div>
                      <div className="group">
                         <div className="w-20 h-20 bg-[#f0f0f0] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E84A27] transition-colors">
                            <Code size={32} className="text-[#13294B] group-hover:text-white transition-colors" />
                         </div>
                         <h3 className="text-2xl font-bold text-[#13294B] mb-4">Computing</h3>
                         <p className="text-gray-600">Home to the National Center for Supercomputing Applications.</p>
                     </div>
                      <div className="group">
                         <div className="w-20 h-20 bg-[#f0f0f0] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E84A27] transition-colors">
                            <Book size={32} className="text-[#13294B] group-hover:text-white transition-colors" />
                         </div>
                         <h3 className="text-2xl font-bold text-[#13294B] mb-4">Excellence</h3>
                         <p className="text-gray-600">Consistently ranked among the top public universities in the nation.</p>
                     </div>
                </div>
            </section>

             {/* News List */}
             <section className="bg-[#f8f9fa] py-24 px-6 md:px-12">
                 <div className="max-w-[1000px] mx-auto">
                     <h2 className="text-3xl font-bold text-[#13294B] mb-12 flex items-center gap-4">
                         {schoolName.split(' ')[0]} News <ArrowRight className="text-[#E84A27]"/>
                     </h2>

                     <div className="space-y-8">
                         {[
                             { date: "OCT 25", title: "Grainger Engineering announces new quantum computing center" },
                             { date: "OCT 24", title: "Agricultural researchers develop drought-resistant crops" },
                             { date: "OCT 22", title: "Gies College of Business launches new online MBA program" }
                         ].map((item, i) => (
                             <div key={i} className="bg-white p-8 rounded-lg shadow-sm flex flex-col md:flex-row gap-6 items-center group cursor-pointer hover:shadow-md transition-shadow">
                                 <div className="bg-[#E84A27]/10 text-[#E84A27] px-4 py-2 rounded-md font-bold text-sm text-center min-w-[80px]">
                                     {item.date}
                                 </div>
                                 <h3 className="text-xl font-bold text-[#13294B] group-hover:text-[#E84A27] transition-colors flex-1">{item.title}</h3>
                                 <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-[#13294B] group-hover:text-white transition-colors">
                                     <ArrowRight size={16}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#13294B] text-white py-16 px-6 md:px-12 border-t-8 border-[#E84A27]">
                  <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 text-sm">
                      <div className="max-w-xs">
                          <div className="flex items-center gap-2 mb-6">
                            <div className="bg-[#E84A27] text-white w-8 h-8 flex items-center justify-center font-serif font-bold text-xl rounded-sm">I</div>
                            <span className="font-bold uppercase tracking-wide text-lg">Illinois</span>
                          </div>
                          <p className="text-gray-400 mb-6">
                              Urbana-Champaign, IL 61801 <br/>
                              (217) 333-1000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-12 font-bold text-gray-300">
                          <ul className="space-y-3 hover:[&_a]:text-[#E84A27]">
                              <li><a href="#">Campus Map</a></li>
                              <li><a href="#">Student Life</a></li>
                              <li><a href="#">Arts & Culture</a></li>
                          </ul>
                          <ul className="space-y-3 hover:[&_a]:text-[#E84A27]">
                              <li><a href="#">Employment</a></li>
                              <li><a href="#">Startups</a></li>
                              <li><a href="#">Technology</a></li>
                          </ul>
                           <ul className="space-y-3 hover:[&_a]:text-[#E84A27]">
                              <li><a href="#">Privacy Policy</a></li>
                              <li><a href="#">Copyright</a></li>
                              <li><a href="#">Accessibility</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500">
                      © 2026 {schoolName} Board of Trustees
                  </div>
             </footer>
        </div>
    );
};

export default UIUC;
